import type {
  QuestionGroupProps,
  QuestionProps,
} from "../../../../../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { createEmptyFlattenResult } from "../utils";

const AGREEMENT_TYPES = {
  DIGITAL_DISTRIBUTION: "Digital distribution",
  BINDING_AGREEMENT: "Binding agreement",
  FRAUD_WARNING: "Fraud Warning",
} as const;

/**
 * consolidated_question_group
 * Includes the primary question and group questions with visibility and answers adjusted based on the primary question's answer.
 */
export const flattenAgreementGroup = (
  group: Partial<QuestionGroupProps>,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];

  // If the group questions are missing, or all the questions are hidden, return an empty flatten group
  if (!groupQuestions || filteredQuestions.every(q => q.visible === false)) {
    return createEmptyFlattenResult(answers, visibility);
  }

  // Map questions with answers and set visibility based on primary answer
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };

  // Build agreement text from all questions
  const agreementItems = filteredQuestions
    .filter(q => q.visible !== false)
    .flatMap(q => {
      const secondaryText =
        typeof q.secondaryText === "string" ? q.secondaryText : "";

      // Determine split pattern based on primary text
      const isDigitalDistribution =
        q.primaryText === AGREEMENT_TYPES.DIGITAL_DISTRIBUTION;
      const isBindingAgreement =
        q.primaryText === AGREEMENT_TYPES.BINDING_AGREEMENT;
      const isFraudWarning = q.primaryText === AGREEMENT_TYPES.FRAUD_WARNING;

      let parts: string[] = [];

      if (isDigitalDistribution) {
        // Split by <br> tags for Digital distribution
        parts = secondaryText
          .split(/<br\s*\/?>/i)
          .map(part => part.trim())
          .filter(part => part.length > 0);
      } else if (isBindingAgreement || isFraudWarning) {
        // Split by periods for Binding agreement and Fraud Warning
        parts = secondaryText
          .split(/\./)
          .map(part => part.trim())
          .filter(part => part.length > 0);
      } else {
        // Default: don't split, use as is
        parts = secondaryText.trim() ? [secondaryText.trim()] : [];
      }

      if (isDigitalDistribution || isBindingAgreement || isFraudWarning) {
        return parts.length > 0
          ? `<p><strong>${q.primaryText}</strong></p><ul>${parts
              .map(part => `<li>${part.trim()}</li>`)
              .join("")}</ul>`
          : "";
      } else {
        return parts.length > 0
          ? `<p><strong>${q.primaryText}</strong></p><ul>${parts
              .map(part => `${part.trim()}`)
              .join("")}</ul>`
          : "";
      }
    })
    .join("\n");

  const agreementText = agreementItems || "";

  // Create a single agreement question that contains all the agreement items
  const agreementAnswer = answers["agreement_question"];
  const groupWithTitle = group as { title?: string };
  const agreementQuestion: Partial<QuestionProps> = {
    id: "agreement_question",
    name: "agreement_question",
    primaryText: groupWithTitle.title || "Agreement",
    secondaryText: agreementText,
    display: {
      type: "agreement",
    },
    visible: true,
    required: true,
    answer: agreementAnswer,
  };

  // Set the answer for the agreement question
  if (agreementAnswer !== undefined) {
    answersByQuestionName["agreement_question"] = agreementAnswer;
  }

  visibilityByQuestionName["agreement_question"] = true;

  console.log("flattenAgreementGroup", {
    agreementQuestion,
    answersByQuestionName,
    visibilityByQuestionName,
    filteredQuestions,
  });

  // Return a single agreement question
  return {
    questions: [agreementQuestion],
    groups: {
      ["agreement"]: {
        kind: "group",
        names: ["agreement_question"],
      },
    },
    visibility: visibilityByQuestionName,
    answers: answersByQuestionName,
  };
};
